import { z } from 'zod';
import { changeAuthorSchema } from '../../history-management/changeAuthor.js';
import { revenueBrandSchema } from '../revenueBrand.js';
import { subscriptionActionsSchema } from './subscriptionActions.js';
import { subscriptionBillingCadenceSchema } from './subscriptionBillingCadence.js';
import { subscriptionChangeCategorySchema, subscriptionChangeTypeSchema, } from './subscriptionChange.js';
import { billingProviderSchema, chargeKindSchema, chargeStatusSchema } from './subscriptionCharge.js';
import { subscriptionDocumentOutputSchema } from './subscriptionDocument.js';
import { subscriptionPaymentMethodSchema } from './subscriptionPaymentMethod.js';
import { subscriptionPredecessorSummarySchema } from './subscriptionPredecessor.js';
import { subscriptionSaleTypeSchema } from './subscriptionSaleType.js';
import { subscriptionStatusSchema } from './subscriptionStatus.js';
// Every amount is net of MWST and travels as a decimal string. Every timestamp is an ISO string.
export const subscriptionPlanSummarySchema = z.object({
    // Both, because the ops tool selects a plan by id while the change log names one by uuid.
    id: z.number().int(),
    uuid: z.uuid(),
    name: z.string(),
    commitmentLengthInMonths: z.number().int(),
});
export const subscriptionAssigneeSchema = z.object({
    id: z.number().int(),
    displayName: z.string(),
    email: z.email(),
});
export const subscriptionContactSchema = z.object({
    id: z.number().int(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.email(),
    phoneNumber: z.string(),
    phoneNumberMobile: z.string().nullable(),
});
export const subscriptionTicketSummarySchema = z.object({
    id: z.number().int(),
    title: z.string(),
    type: z.string(),
});
export const billingPeriodInvoiceSchema = z.discriminatedUnion('provider', [
    z.object({
        provider: z.literal(billingProviderSchema.enum.Stripe),
        stripeInvoiceId: z.string().nullable(),
        hasDocument: z.boolean(),
        // Set on a correction once Accounting has issued a credit note for the refund, which is the document the
        // partner is given. A refund on its own carries no number.
        creditNoteNumber: z.string().nullable(),
        attemptCount: z.number().int().nullable(),
        nextAttemptAt: z.iso.datetime().nullable(),
    }),
    z.object({
        provider: z.literal(billingProviderSchema.enum.Bexio),
        invoiceNumber: z.string(),
        // Prefixed with the Bexio account the document lives in, e.g. "Zurich:1234".
        externalId: z.string(),
        status: z.string(),
        invoiceDate: z.iso.datetime(),
        dueDate: z.iso.datetime(),
        lastPaymentDate: z.iso.datetime().nullable(),
    }),
]);
export const billingPeriodDocumentLinkSchema = z.object({
    redirectUrl: z.string(),
});
export const billingPeriodSchema = z.object({
    uuid: z.uuid(),
    kind: chargeKindSchema,
    status: chargeStatusSchema,
    provider: billingProviderSchema,
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
    netAmount: z.string(),
    discountAmount: z.string(),
    // Zero until the document has been synced back, because that is when the rate becomes known.
    taxAmount: z.string(),
    totalAmount: z.string(),
    amountDue: z.string(),
    amountPaid: z.string(),
    issuedAt: z.iso.datetime().nullable(),
    dueAt: z.iso.datetime().nullable(),
    paidAt: z.iso.datetime().nullable(),
    revenueBrand: revenueBrandSchema,
    // Who the charge belonged to when it was written, not who holds the subscription now.
    salesAssignee: subscriptionAssigneeSchema.nullable(),
    telesalesAssignee: subscriptionAssigneeSchema.nullable(),
    invoice: billingPeriodInvoiceSchema.nullable(),
    invoiceGenerationFailedAt: z.iso.datetime().nullable(),
    correctsBillingPeriodUuid: z.uuid().nullable(),
});
export const commitmentPeriodSchema = z.object({
    sequence: z.number().int(),
    startedAt: z.iso.datetime(),
    // Taken from the next period's start for a closed period and from the subscription for the open one, because free
    // time and plan changes move the boundary after the log entry was written.
    endsAt: z.iso.datetime(),
    lengthInMonths: z.number().int(),
    billingCadence: subscriptionBillingCadenceSchema,
    pricePerMonth: z.string(),
    amountPerCharge: z.string(),
    plan: subscriptionPlanSummarySchema.nullable(),
    isCurrent: z.boolean(),
    billingPeriods: billingPeriodSchema.array(),
});
const upcomingChangeFields = {
    effectiveAt: z.iso.datetime(),
    changeAuthor: changeAuthorSchema,
    comment: z.string().nullable(),
};
export const upcomingSubscriptionChangeSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('PlanChange'),
        ...upcomingChangeFields,
        previousPlan: subscriptionPlanSummarySchema.nullable(),
        newPlan: subscriptionPlanSummarySchema.nullable(),
        previousAmountPerCharge: z.string(),
        newAmountPerCharge: z.string(),
        previousBillingCadence: subscriptionBillingCadenceSchema,
        newBillingCadence: subscriptionBillingCadenceSchema,
    }),
    z.object({
        kind: z.literal('FreeTime'),
        ...upcomingChangeFields,
        days: z.number().int(),
        coveredFrom: z.iso.datetime(),
        coveredUntil: z.iso.datetime(),
    }),
    z.object({
        kind: z.literal('Cancellation'),
        ...upcomingChangeFields,
        // The cancellation ends a credits coverage window rather than a committed term, so nothing is ever billed.
        endsCoveredPeriod: z.boolean(),
    }),
]);
export const subscriptionDetailSchema = z.object({
    id: z.number().int(),
    uuid: z.uuid(),
    status: subscriptionStatusSchema,
    plan: subscriptionPlanSummarySchema.nullable(),
    paymentMethod: subscriptionPaymentMethodSchema,
    billingCadence: subscriptionBillingCadenceSchema,
    saleType: subscriptionSaleTypeSchema,
    revenueBrand: revenueBrandSchema,
    pricePerMonth: z.string(),
    upfrontPrice: z.string().nullable(),
    commitmentLengthInMonths: z.number().int(),
    firstPayment: z.string(),
    commitmentTotal: z.string(),
    // 1 is the first commitment, 2 the first prolongation.
    commitmentSequence: z.number().int(),
    autoProlongation: z.boolean(),
    // The day it was sold to start on, null for as soon as validation passes.
    agreedStartAt: z.iso.datetime().nullable(),
    initialFreeDays: z.number().int(),
    // Days the migrated contract's remaining value pays for, opening before the first committed term.
    creditsCoverageDays: z.number().int(),
    // When validation passed and collection began. Not the start, which waits for the first payment to land.
    billingInitiatedAt: z.iso.datetime().nullable(),
    startedAt: z.iso.datetime().nullable(),
    commitmentStartedAt: z.iso.datetime().nullable(),
    commitmentEndsAt: z.iso.datetime().nullable(),
    termsVersion: z.string().nullable(),
    termsAcceptedAt: z.iso.datetime().nullable(),
    validationRejectedAt: z.iso.datetime().nullable(),
    // The most recent link sent to the partner.
    checkoutLink: z
        .object({
        sentTo: z.email(),
        sentAt: z.iso.datetime(),
        validUntil: z.iso.datetime(),
        usable: z.boolean(),
    })
        .nullable(),
    cancellationRequestedAt: z.iso.datetime().nullable(),
    cancelEffectiveAt: z.iso.datetime().nullable(),
    terminatedAt: z.iso.datetime().nullable(),
    // Derived, and empty once a cancellation or termination is in place.
    nextDueAt: z.iso.datetime().nullable(),
    nextProlongationAt: z.iso.datetime().nullable(),
    salesAssignee: subscriptionAssigneeSchema.nullable(),
    telesalesAssignee: subscriptionAssigneeSchema.nullable(),
    partnerContact: subscriptionContactSchema.nullable(),
    ticket: subscriptionTicketSummarySchema.nullable(),
    predecessor: subscriptionPredecessorSummarySchema.nullable(),
    comment: z.string().nullable(),
    documents: subscriptionDocumentOutputSchema.array(),
    commitmentPeriods: commitmentPeriodSchema.array(),
    // A term only begins once the first payment lands, but a charge has to be written before that, because nobody can
    // pay what they were never billed for. Everything billed and not yet paid falls here.
    unassignedBillingPeriods: billingPeriodSchema.array(),
    upcomingChanges: upcomingSubscriptionChangeSchema.array(),
    actions: subscriptionActionsSchema,
});
export const subscriptionHistoryEntryBaseSchema = z.object({
    id: z.number().int(),
    uuid: z.uuid(),
    type: subscriptionChangeTypeSchema,
    created: z.iso.datetime(),
    // In the future while the change is still pending.
    effectiveAt: z.iso.datetime(),
    changeAuthor: changeAuthorSchema,
    revenueBrand: revenueBrandSchema,
    comment: z.string().nullable(),
    // Set on the three revocation types, which carry only the uuid of the entry they undo.
    revokes: z
        .object({
        uuid: z.uuid(),
        type: subscriptionChangeTypeSchema,
        effectiveAt: z.iso.datetime(),
    })
        .nullable(),
});
export const SUBSCRIPTION_HISTORY_PAGE_SIZE = 25;
export const subscriptionHistoryQuerySchema = z.object({
    type: subscriptionChangeTypeSchema.optional(),
    category: subscriptionChangeCategorySchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    // The id of the last entry of the previous page.
    cursorId: z.coerce.number().int().positive().optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
});
